import { Fragment, useContext, useMemo, useState } from 'react'
import useMediaSelectionActions from '../../actions/useMediaSelectionActions'
import useMediaSourceViews from '../../actions/useMediaSourceViews'
import GearIcon from '../../CustomIcons/GearIcon'
import PlusIcon from '../../CustomIcons/PlusIcon'
import { Button } from '../../UIComponents2/Button'
import { Checkbox } from '../../UIComponents2/Checkbox'
import {
    AudioInputSelection,
    AudioSourceInfo,
    AudioTrackWithInfo,
    UserContext,
    UserContextType,
} from '../../UserContext'
import { cn } from '../../utlis/cn'
import { AudioQualitySelect, qualityPresetToLabel } from '../AudioQualitySelect'
import * as Constants from '../../services/Constants'
import { VolumeMeter } from '@/components/VolumeMeter'
import { ChannelSelect } from './ChannelSelect'
import Tooltip from '@/UIComponents2/Tooltip'
import _ from 'lodash'
import { useTranslation } from 'react-i18next'
import AudioInputDeviceSettingsDialog from '@/pages/AudioInputDeviceSettings/AudioInputDeviceSettingsDialog'

type AudioInputSelectionPanelProps = {
    noHeader?: React.ReactNode
    className?: string
}
export function AudioInputSelectionPanel(props: AudioInputSelectionPanelProps) {
    const { audioInputsClassifiedForUi } = useMediaSourceViews()
    const [inputDeviceSettings, setInputDeviceSettings] = useState<AudioSourceInfo | null>(null)

    let systemAudioInputs = audioInputsClassifiedForUi['external']

    return (
        <>
            <div className={cn('grid grid-cols-[45%_1fr_33%]', props.className)}>
                {!props.noHeader && <Header />}
                {systemAudioInputs.map((source, i) => {
                    const isLastItem = i === systemAudioInputs.length - 1
                    // const isTalkback = source.deviceId === userState.mediaSelection.talkbackMic
                    return (
                        <DeviceRow
                            key={source.deviceId}
                            source={source}
                            last={isLastItem}
                            onOpenSettings={() => setInputDeviceSettings(source)}
                        />
                    )
                })}
            </div>
            {inputDeviceSettings && (
                <AudioInputDeviceSettingsDialog
                    mediaSource={inputDeviceSettings}
                    onClose={() => setInputDeviceSettings(null)}
                />
            )}
        </>
    )
}

function Header({ customHeader }: { customHeader?: React.ReactNode }) {
    const { t } = useTranslation()
    const border = 'border-b border-[rgba(255,255,255,0.03)] pb-3'
    return (
        <>
            <div className={cn(border, 't-base-medium')}>{customHeader ? customHeader : t('Device List')}</div>
            <div className={cn(border, 't-base-light opacity-75')}>{t('Channels')}</div>
            <div className={cn(border, 't-base-light opacity-75')}>{t('Quality')}</div>
        </>
    )
}

type DeviceRowProps = {
    source: AudioSourceInfo
    last: boolean
    onOpenSettings: () => void
}
function DeviceRow(props: DeviceRowProps) {
    const { t } = useTranslation()
    const { toggleAudioDeviceSelection, updateSelectedAudioDeviceQualityPreset } = useMediaSelectionActions()
    const { state: userState } = useContext(UserContext) as UserContextType

    const [showNewChannelGroupRow, setShowNewChannelGroupRow] = useState(false)

    const selection = userState.mediaSelection.audioInputs.find((i) => i.deviceId === props.source.deviceId) || null
    const atrack = userState.mediaStreams.audioInputs.find((i) => i.info.deviceId === selection?.deviceId)
    const isSelected = selection != null
    const isTalkback = userState.mediaSelection.talkbackMic === props.source.deviceId

    const channelGroups = useMemo(() => {
        const groups = _.clone(selection?.settings.channelSelection) || [[0]]
        if (showNewChannelGroupRow) {
            groups.push([])
        }
        return groups
    }, [showNewChannelGroupRow, selection])

    const currQualityPresetCode = selection ? Constants.settingsToAudioQualityPesetCode(selection.settings) : 'voice'

    const handleChangeQuality = (code: Constants.AudioQualityPresetCode) => {
        if (selection) {
            updateSelectedAudioDeviceQualityPreset(selection, code)
        }
    }

    const handleToggle = () => {
        toggleAudioDeviceSelection(props.source)
    }

    const renderQualitySelect = (first: boolean) => {
        if (!isSelected) {
            return null
        }
        if (first) {
            return (
                <>
                    <AudioQualitySelect
                        value={currQualityPresetCode}
                        onChange={(code) => handleChangeQuality(code)}
                        className="py-[10px] w-full pl-2"
                        channelGroupsShared={channelGroups.length}
                        isTalkback={isTalkback}
                    />
                    <Button className="opacity-75 hover:opacity-100" onClick={() => props.onOpenSettings()}>
                        <GearIcon />
                    </Button>
                </>
            )
        }

        return <div className="pl-4 t-base-light">{qualityPresetToLabel(currQualityPresetCode, t)}</div>
    }

    let sharedClasses = 'h-[54px]'
    if (!props.last) {
        sharedClasses += ' border-b border-[rgba(255,255,255,0.03)]'
    }

    return (
        <>
            {channelGroups.map((chgrp, i) => {
                const first = i === 0
                const last = i === channelGroups.length - 1
                const key = chgrp.join('-')

                return (
                    <Fragment key={key}>
                        <div className={cn(sharedClasses, 'flex items-center gap-2 pl-0')}>
                            {first && (
                                <Checkbox
                                    checked={isSelected}
                                    onChange={() => handleToggle()}
                                    label={props.source.displayLabel}
                                />
                            )}
                        </div>
                        <div className={cn(sharedClasses, 'flex items-center')}>
                            {selection && isSelected && (
                                <ChannelGroupItem
                                    chgrp={chgrp}
                                    atrack={atrack}
                                    sharedGroups={channelGroups}
                                    last={last}
                                    selection={selection}
                                    onShowNewChannelGroup={(val) => setShowNewChannelGroupRow(val)}
                                    addingNewChannelGroup={showNewChannelGroupRow}
                                />
                            )}
                        </div>
                        <div className={cn(sharedClasses, 'flex pr-0 gap-2 w-full items-center')}>
                            {renderQualitySelect(first)}
                        </div>
                    </Fragment>
                )
            })}
        </>
    )
}

type ChannelSelectProps = {
    sharedGroups: number[][]
    chgrp: number[]
    last: boolean
    selection: AudioInputSelection
    onShowNewChannelGroup: (show: boolean) => void
    addingNewChannelGroup: boolean
    atrack: AudioTrackWithInfo | undefined
}
function ChannelGroupItem(props: ChannelSelectProps) {
    const {
        updateSelectedAudioDeviceAddChannelGroup,
        updateSelectedAudioDeviceReplaceChannelGroup,
        updateSelectedAudioDeviceRemoveChannelGroup,
    } = useMediaSelectionActions()

    const handleAddChannelGroup = (chgrp: number[]) => {
        if (props.selection) {
            updateSelectedAudioDeviceAddChannelGroup(props.selection, chgrp)
        }
    }

    const handleReplaceChannelGroup = (prevChgrp: number[], newChgrp: number[]) => {
        if (props.selection) {
            updateSelectedAudioDeviceReplaceChannelGroup(props.selection, prevChgrp, newChgrp)
        }
    }

    const handleRemoveChannelGroup = (chgrp: number[]) => {
        if (props.selection) {
            updateSelectedAudioDeviceRemoveChannelGroup(props.selection, chgrp)
        }
    }

    const allowMultipleGroups = props.selection.settings.isAudioEngine
    const maxChannel = props.selection.settings.isVoiceQuality ? 1 : 255
    const isNewRow = props.addingNewChannelGroup && props.last
    const allChannelsShared =
        props.sharedGroups.reduce((a, b) => a + b.length, 0) >= props.selection.source.maxInputChannels
    const canAddChannelGroup = props.last && allowMultipleGroups && !props.addingNewChannelGroup && !allChannelsShared

    const newChannelGroupTooltip = useMemo(() => {
        if (!allowMultipleGroups) {
            return 'Change to pro audio quality to share more channels'
        }
        if (props.addingNewChannelGroup) {
            return 'Adding channel group'
        }
        if (allChannelsShared) {
            return 'All channels are already shared'
        }

        return 'Share more channels'
    }, [allowMultipleGroups, props.addingNewChannelGroup, allChannelsShared])

    return (
        <div className={cn('flex items-center gap-1 pl-2')}>
            <div className="w-[14px]">
                {props.atrack && !isNewRow ? <VolumeMeter stream={props.atrack} variant="pills" /> : null}
            </div>
            <ChannelSelect
                defaultOpen={isNewRow}
                numChannels={props.selection.source.maxInputChannels}
                maxChannel={maxChannel}
                mergedChannels={props.selection.settings.isVoiceQuality}
                value={props.chgrp}
                sharedGroups={props.sharedGroups}
                onClose={() => setTimeout(() => props.onShowNewChannelGroup(false))}
                onChange={(v) => {
                    if (!v || v.value.length === 0) {
                        return
                    }
                    if (isNewRow) {
                        handleAddChannelGroup(v.value)
                    } else {
                        handleReplaceChannelGroup(props.chgrp, v.value)
                    }
                }}
                onRemove={() => handleRemoveChannelGroup(props.chgrp)}
                triggerClassName="min-w-[70px]"
            />
            <Tooltip text={newChannelGroupTooltip}>
                <Button
                    disabled={!canAddChannelGroup}
                    className={cn('opacity-75', !canAddChannelGroup && 'cursor-not-allowed')}
                    onClick={() => props.onShowNewChannelGroup(true)}
                >
                    <PlusIcon />
                </Button>
            </Tooltip>
        </div>
    )
}
